import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import type { BulkUserOutcome, BulkUsersResponse, InviteManyOutcome, InviteManyResult } from '@nest-recruit/shared';
import { ToneChip, type Tone } from '../../../../components/Tone';
import styles from './ResultList.module.css';

/**
 * What happened to each person or address after a bulk action or a multi-address invite: the totals first, then
 * one line each, with the rule's own words where something was skipped. Nothing is summarised away — a person
 * who was skipped is named, so the Admin knows whom to look at.
 */

export interface ResultLine {
  readonly key: string;
  readonly name: string;
  readonly outcome: string;
  readonly tone: Tone;
  readonly message: string | null;
  /** Copy link on an invited address. */
  readonly action?: ReactNode;
}

export function ResultList({
  title,
  totals,
  lines,
  onDismiss,
}: {
  readonly title: string;
  readonly totals: string;
  readonly lines: readonly ResultLine[];
  readonly onDismiss?: () => void;
}): ReactNode {
  return (
    <section className={styles.panel} aria-label={title}>
      <header className={styles.head}>
        <span className={styles.headText}>
          <h3 className={styles.title}>{title}</h3>
          <span className={styles.totals}>{totals}</span>
        </span>
        {onDismiss && (
          <button type="button" className={styles.dismiss} onClick={onDismiss} aria-label="Dismiss the results">
            <X size={16} aria-hidden />
          </button>
        )}
      </header>
      <ul className={styles.lines}>
        {lines.map((line) => (
          <li key={line.key} className={styles.line}>
            <span className={styles.lineText}>
              <span className={styles.name}>{line.name}</span>
              {line.message !== null && <span className={styles.message}>{line.message}</span>}
            </span>
            {line.action}
            <ToneChip tone={line.tone}>{line.outcome}</ToneChip>
          </li>
        ))}
      </ul>
    </section>
  );
}

const BULK_OUTCOMES: Readonly<Record<BulkUserOutcome, { readonly label: string; readonly tone: Tone }>> = {
  changed: { label: 'Done', tone: 'done' },
  skipped: { label: 'Skipped', tone: 'attention' },
  failed: { label: 'Failed', tone: 'urgent' },
};

/** "8 changed · 2 skipped · 1 failed", leaving out the zeros after the first. */
export function bulkTotals(response: BulkUsersResponse): string {
  const parts = [`${response.changed} changed`];
  if (response.skipped > 0) parts.push(`${response.skipped} skipped`);
  if (response.failed > 0) parts.push(`${response.failed} failed`);
  return parts.join(' · ');
}

/** Skipped and failed first: they are the ones that need a look. */
export function bulkLines(response: BulkUsersResponse): readonly ResultLine[] {
  const order: Readonly<Record<BulkUserOutcome, number>> = { failed: 0, skipped: 1, changed: 2 };
  return [...response.results]
    .sort((a, b) => order[a.outcome] - order[b.outcome])
    .map((result) => ({
      key: result.userId,
      name: result.fullName,
      outcome: BULK_OUTCOMES[result.outcome].label,
      tone: BULK_OUTCOMES[result.outcome].tone,
      message: result.message,
    }));
}

export const INVITE_OUTCOMES: Readonly<Record<InviteManyOutcome, { readonly label: string; readonly tone: Tone }>> = {
  invited: { label: 'Invited', tone: 'done' },
  invalid: { label: 'Not an email', tone: 'urgent' },
  duplicate: { label: 'Typed twice', tone: 'attention' },
  already_user: { label: 'Already a user', tone: 'attention' },
  already_invited: { label: 'Already invited', tone: 'attention' },
  failed: { label: 'Failed', tone: 'urgent' },
};

export function inviteTotals(results: readonly InviteManyResult[]): string {
  const invited = results.filter((result) => result.outcome === 'invited').length;
  const other = results.length - invited;
  return other === 0 ? `${invited} invited` : `${invited} invited · ${other} not sent`;
}
